import React from "react";
import LegalFooterBannerAndLinks from "components/legal/LegalFooterBannerAndLinks";
import LegalPagesBrandHeader from "components/legal/LegalPagesBrandHeader";

const analysisBlocks = [
  {
    title: "Technical Structure Analysis",
    points: [
      "Market structure, trend continuation, and reversal zones",
      "Multi-timeframe context (higher timeframe bias + lower timeframe execution)",
      "Liquidity areas, invalidation levels, and volatility behavior",
      "Confluence between price action and execution quality",
    ],
  },
  {
    title: "Execution Context",
    points: [
      "Session timing and liquidity windows",
      "Spread/slippage sensitivity around entries",
      "Risk-to-reward profile before activation",
      "Fallback rules when market conditions degrade",
    ],
  },
  {
    title: "Copy Activation Logic",
    points: [
      "Master-account trade validation before replication",
      "Eligibility checks per client slot/account status",
      "Predefined invalidation and risk boundaries",
      "Execution queue rules during high-volatility windows",
    ],
  },
  {
    title: "Risk Management Framework",
    points: [
      "Per-trade risk control and lot-size discipline",
      "Scenario-based drawdown management",
      "Conservative profile option (e.g. 0.01 lot sizing)",
      "Ongoing performance review and strategy refinement",
    ],
  },
];

const copyFormat = [
  { key: "Strategy Trade", value: "Validated trade opened on our strategy account" },
  { key: "Auto Replication", value: "Trade copied automatically to connected client accounts" },
  { key: "Risk Profile", value: "Client-side lot sizing / risk factor configuration" },
  { key: "Protection Rules", value: "System-level stop logic and exposure controls" },
];

const limitations = [
  "Market conditions can change quickly after execution on our strategy account.",
  "Execution prices may differ across brokers due to spread and slippage.",
  "Past performance does not guarantee future results.",
  "Not every copied trade will be profitable; losses are part of trading.",
  "Individual outcomes vary based on risk settings, timing, and infrastructure.",
  "Where a first-month refund guarantee applies, eligibility requires an AlphaSniper-designated broker partner account as defined in the Refund Policy—not every broker or account type qualifies.",
];

export default function Methodology() {
  return (
    <main className="min-h-screen bg-[#0B1437] px-5 py-6 text-white sm:py-8">
      <div className="mx-auto w-full max-w-5xl">
        <LegalPagesBrandHeader />
        <header className="mb-5 space-y-2 text-center">
          <h1 className="text-3xl font-bold sm:text-4xl">
            Our Trading Methodology
          </h1>
          <p className="mx-auto max-w-3xl text-sm text-gray-300">
            Transparency in how we analyze markets and structure trade execution.
          </p>
          <p className="mx-auto max-w-3xl text-sm leading-relaxed text-gray-300 sm:text-base">
            AlphaSniper combines semi-automated strategy logic with automated
            copy execution. Our objective is to deliver consistent structure,
            clear risk controls, and practical execution for clients who do not
            want to manually execute trades.
          </p>
        </header>

        <section className="space-y-4">
          {analysisBlocks.map((block) => (
            <article
              key={block.title}
              className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 sm:p-6"
            >
              <h2 className="text-lg font-semibold text-white">{block.title}</h2>
              <ul className="mt-3 space-y-2 text-sm text-gray-300 sm:text-base">
                {block.points.map((point) => (
                  <li key={point} className="leading-relaxed">
                    • {point}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </section>

        <section className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-5 sm:p-6">
          <h2 className="text-lg font-semibold text-white">
            What the Auto-Copy Flow Includes
          </h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {copyFormat.map((item) => (
              <div
                key={item.key}
                className="rounded-xl border border-white/10 bg-[#0a102c]/60 p-4"
              >
                <p className="text-sm font-semibold text-brand-300">{item.key}</p>
                <p className="mt-1 text-sm text-gray-300">{item.value}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-6 rounded-2xl border border-amber-300/20 bg-amber-500/[0.06] p-5 sm:p-6">
          <h2 className="text-lg font-semibold text-amber-200">
            Important Limitations
          </h2>
          <ul className="mt-3 space-y-2 text-sm leading-relaxed text-amber-100/90 sm:text-base">
            {limitations.map((item) => (
              <li key={item}>• {item}</li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-amber-100/80 sm:text-sm">
            For full legal disclosures, please review our Risk Disclaimer and
            Privacy Policy pages.
          </p>
        </section>

        <footer className="mt-8 border-t border-white/10 pt-5 text-xs text-gray-400 sm:text-sm">
          <p className="text-center">
            AlphaSniper 2026 © All Rights Reserved
          </p>
          <LegalFooterBannerAndLinks />
        </footer>
      </div>
    </main>
  );
}
